import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  pageShippingCenterOrders, 
  manualShipOrder, 
  autoShipOrder 
} from '../../api/shippingCenter';
import { 
  cancelShippingOrder,
  reprintShippingOrder  // 新增的重新打印发货单 API
} from '../../api/shipping';
import { message } from 'antd';

// 获取发货中心订单列表
export const fetchShippingCenterOrders = createAsyncThunk(
  'shippingCenter/fetchOrders',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await pageShippingCenterOrders(params);
      return response.data;
    } catch (error) {
      message.error('获取发货中心订单列表失败');
      return rejectWithValue(error.response.data);
    }
  }
);

// 手动发货
export const performManualShipOrder = createAsyncThunk(
  'shippingCenter/manualShipOrder',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await manualShipOrder({
        orderId: data.orderId,
        logisticsCompanyId: data.logisticsCompanyId,
        trackingNumber: data.trackingNumber,
        senderId: data.senderId,
        senderName: data.senderName,
        senderPhone: data.senderPhone,
        senderAddress: data.senderAddress
      });
      
      message.success('手动发货成功');
      // 发货成功后重新获取列表
      dispatch(fetchShippingCenterOrders());
      
      return response.data;
    } catch (error) {
      // 详细的错误处理
      const errorMessage = error.response?.data?.message || 
                           error.message || 
                           '手动发货失败';
      
      message.error(errorMessage);
      
      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status,
        data: error.response?.data
      });
    }
  }
);

// 自动发货（打单发货）
export const performAutoShipOrder = createAsyncThunk(
  'shippingCenter/autoShipOrder',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await autoShipOrder({
        orderId: data.orderId,
        logisticsCompanyId: data.logisticsCompanyId,
        senderId: data.senderId,
        printTemplateId: data.printTemplateId,
        printChannel: data.printChannel,
        deviceId: data.deviceId,
        printerId: data.printerId
      });
      
      message.success('打单发货成功');
      // 发货成功后重新获取列表
      dispatch(fetchShippingCenterOrders());
      
      return response.data;
    } catch (error) {
      // 详细的错误处理
      const errorMessage = error.response?.data?.message || 
                           error.message || 
                           '打单发货失败';
      
      message.error(errorMessage);
      
      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status,
        data: error.response?.data
      });
    }
  }
);

// 取消订单发货
export const cancelShipping = createAsyncThunk(
  'shippingCenter/cancelShipping',
  async (orderId, { rejectWithValue, dispatch }) => {
    try {
      const response = await cancelShippingOrder(orderId);
      
      message.success('取消发货成功');
      // 取消发货成功后重新获取列表
      dispatch(fetchShippingCenterOrders());
      
      return response.data;
    } catch (error) {
      // 详细的错误处理
      const errorMessage = error.response?.data?.message || 
                           error.message || 
                           '取消发货失败';
      
      message.error(errorMessage);
      
      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status,
        data: error.response?.data
      });
    }
  }
);

// 重新打印发货单
export const performReprintShippingOrder = createAsyncThunk(
  'shippingCenter/reprintShippingOrder',
  async (params, { rejectWithValue }) => {
    try {
      const response = await reprintShippingOrder(params);
      
      message.success('重新打印发货单成功');
      
      return response.data;
    } catch (error) {
      // 详细的错误处理
      const errorMessage = error.response?.data?.message || 
                           error.message || 
                           '重新打印发货单失败';
      
      message.error(errorMessage);
      
      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status,
        data: error.response?.data
      });
    }
  }
);

const shippingCenterSlice = createSlice({
  name: 'shippingCenter',
  initialState: {
    orders: [],
    total: 0,
    currentPage: 1,
    pageSize: 10,
    loading: false,
    error: null
  },
  reducers: {
    setPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    }
  },
  extraReducers: (builder) => {
    // 获取订单列表
    builder.addCase(fetchShippingCenterOrders.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchShippingCenterOrders.fulfilled, (state, action) => {
      state.loading = false;
      state.orders = action.payload.records;
      state.total = action.payload.total;
    });
    builder.addCase(fetchShippingCenterOrders.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 手动发货
    builder.addCase(performManualShipOrder.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(performManualShipOrder.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(performManualShipOrder.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 自动发货
    builder.addCase(performAutoShipOrder.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(performAutoShipOrder.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(performAutoShipOrder.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 取消发货
    builder.addCase(cancelShipping.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(cancelShipping.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(cancelShipping.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 重新打印发货单
    builder.addCase(performReprintShippingOrder.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(performReprintShippingOrder.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(performReprintShippingOrder.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  }
});

export const { setPage, setPageSize } = shippingCenterSlice.actions;
export default shippingCenterSlice.reducer;
